import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Breakpoints } from 'utils/const.styles';

export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	width: 100%;
	overflow: auto;
	background-color: var(--white);
	border: 1px solid var(--border-color);
	border-radius: var(--small-border-radius);
`;

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	overflow: auto;
	width: 100%;
	height: 100%;

	@media (hover: hover) {
		.resizer {
			opacity: 0;
		}

		*:hover > .resizer {
			opacity: 1;
		}
	}
`;

export const TableScrollContainer = styled.div`
	height: 100%;
	overflow: auto;
`;

export const Table = styled.table`
	width: 100%;
	border-spacing: 0;
`;

export const Thead = styled.thead`
	background: var(--background-3);
	position: sticky;
	top: 0;
	z-index: var(--z-index-3);
`;

export const Tbody = styled.tbody`
	background: var(--white);
`;

export const Tr = styled.tr`
	width: fit-content;

	&.context-menu {
		cursor: pointer;
	}

	&.selected {
		background-color: var(--table-active-color);
	}

	:has(.error-message-container) {
		td {
			vertical-align: top;
		}
	}
`;

export const Th = styled.th`
	position: relative;
	padding: var(--padding-3) var(--padding-2);
	border-bottom: 1px solid var(--border-color);
	text-align: left;

	body[dir='rtl'] & {
		text-align: right;
	}
`;

export const Td = styled.td`
	font-size: var(--paragraph3-font-size);
	padding: var(--padding-2);
	color: var(--black);
	position: relative;

	:has(.error-message-container) {
		> div {
			flex-direction: column;
		}
	}

	&.tree-cell {
		padding: var(--padding-1);
	}

	&.leftBorder {
		:before {
			display: block;
			content: '';
			height: 100%;
			position: absolute;
			top: 0;
			left: 1.5rem;
			border-left: 1px solid var(--blue);

			body[dir='rtl'] & {
				left: unset;
				right: 1.5rem;
			}
		}
	}

	&.halfLeftBorder {
		:before {
			display: block;
			content: '';
			height: 50%;
			position: absolute;
			top: 0;
			left: 1.5rem;
			border-left: 1px solid var(--blue);

			body[dir='rtl'] & {
				left: unset;
				right: 1.5rem;
			}
		}
	}

	&.expanded {
		background-color: var(--background-3);
	}

	@media ${Breakpoints.mobile} {
		border-bottom: 1px solid var(--border-color);
	}
`;

export const CellContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	overflow: auto;
	width: inherit;
	min-width: -webkit-fill-available;
	${({ padding, expandable }) => padding && `padding-left: ${expandable ? padding - 3 : padding}rem;`}

	body[dir='rtl'] & {
		padding-left: unset;
		${({ padding, expandable }) => padding && `padding-right: ${expandable ? padding - 3 : padding}rem;`}
	}

	> * {
		width: 100%;
	}

	@media ${Breakpoints.mobile} {
		${({ padding, expandable }) => padding && `padding-left: ${expandable ? padding - 3 : padding - 1}rem;`}

		body[dir='rtl'] & {
			padding-left: unset;
			${({ padding, expandable }) => padding && `padding-right: ${expandable ? padding - 3 : padding - 1}rem;`}
		}
	}
`;

export const HeaderWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	width: inherit;
	min-width: -webkit-fill-available;

	.header-filter {
		padding: 0;
		width: 100%;

		.table-string-input,
		.table-number-input,
		.table-float-input,
		.enumselect-label,
		.multiselect-label {
			max-height: 3.5rem;
			min-height: 3.5rem;
			height: 100%;
			padding: 0.45rem 0.5rem;

			body[dir='rtl'] & {
				max-height: 4.5rem;
				min-height: 4.5rem;
				padding: 0.75rem 0.5rem;
			}
		}

		.alt-input {
			padding: 0.75rem 0.5rem 0.75rem 3rem !important;

			body[dir='rtl'] & {
				padding: 0.75rem 3rem 0.75rem 0.5rem !important;
			}
		}
	}
`;

export const Header = styled.div`
	display: flex;
	justify-content: space-between;
	&.sortable {
		cursor: pointer;
		user-select: none;
	}
`;

export const HeaderFilterContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: 100%;
`;

export const Resizer = styled.div`
	position: absolute;
	right: 0;
	top: 50%;
	transform: translate(-50%, -50%);
	width: 0.3rem;
	height: 50%;
	background-color: var(--black-20);
	cursor: col-resize;
	user-select: none;
	touch-action: none;

	.isResizing {
		background: var(--content-4);
		opacity: 1;
	}

	@media ${Breakpoints.mobile} {
		display: none;
	}
`;

export const HeaderContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
	overflow: auto;

	&.expanded {
		> .typography {
			font-weight: 500;
		}
	}
`;

export const ExpandedColumnContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	width: 100%;
	overflow: auto;
`;

export const ExpandedRowContainer = styled.div`
	display: grid;
	${({ customExpansionGrid }) =>
		customExpansionGrid
			? `grid-template-columns: ${customExpansionGrid.desktop};`
			: `grid-template-columns: repeat(6, 1fr);`}
	width: 100%;
	gap: 2rem;
	padding: 1.5rem;

	&.edit-mode {
		grid-template-columns: 1fr;
	}

	@media ${Breakpoints.tablet} {
		${({ customExpansionGrid }) =>
			customExpansionGrid
				? `grid-template-columns: ${customExpansionGrid.tablet};`
				: `grid-template-columns: repeat(4, 1fr);`}
	}

	@media ${Breakpoints.mobile} {
		grid-template-columns: 1fr 1fr;
		${({ customExpansionGrid }) =>
			customExpansionGrid
				? `grid-template-columns: ${customExpansionGrid.mobile};`
				: `grid-template-columns: repeat(2, 1fr);`}
		padding: 0;

		&.edit-mode {
			grid-template-columns: 1fr 1fr;
		}
	}
`;

export const FilterContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	flex-wrap: wrap;
	align-items: center;
	gap: 1rem;
	padding: 1rem;

	@media ${Breakpoints.mobile} {
		display: none;
	}
`;

export const FilterValue = styled.span`
	max-width: 20rem;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
`;

export const RemoveFilterButtonCss = css`
	width: 4rem;
	height: 4rem;
	text-decoration: underline;

	> .icon {
		font-size: 2rem !important;
	}
`;

export const MenuWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1rem;

	> div > svg {
		display: none;
	}

	> div > button {
		margin-right: var(--margin-1);

		body[dir='rtl'] & {
			margin-right: unset;
			margin-left: var(--margin-1);
		}
	}

	@media ${Breakpoints.mobile} {
		> div {
			width: 100% !important;
			margin: 0 !important;
		}
	}
`;

export const MobileFilterMenu = styled.div`
	display: flex;
	flex-direction: column;
	box-shadow: var(--container-box-shadow);
	padding: var(--padding-2) 0;
	background-color: var(--modal-bg-3);
	border-radius: var(--small-border-radius);
	min-width: 20rem;
	z-index: var(--z-index-2);
	margin: 0 0 auto var(--margin-2);

	body[dir='rtl'] & {
		margin: 0 var(--margin-2) auto 0;
	}

	&.leftSide {
		margin: 0 var(--margin-2) auto 0;

		body[dir='rtl'] & {
			margin: 0 0 auto var(--margin-2);
		}
	}

	> .selected {
		background-color: var(--table-selected-color);
	}

	&.columnsMenu {
		max-height: 44rem;
		overflow: auto;
	}

	&.filterOptions {
		min-width: 30rem;
		max-width: 48rem;

		@media ${Breakpoints.mobile} {
			max-width: 100%;
			min-width: unset;
		}
	}
`;

export const FilterMenuItemButtonCss = css`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	min-height: 6rem;
	border-top: 1px solid var(--border-color) !important;
	padding: var(--padding-3) !important;

	&.expanded {
		> .expandIcon {
			transform: rotate(90deg);

			body[dir='rtl'] & {
				transform: rotate(90deg);
			}
		}
	}
`;

export const FilterColumnsButtonContainer = styled.span`
	display: flex;
	justify-content: flex-end;
	flex-direction: row;
	padding: 1rem;
	border-top: 1px solid var(--border-color);
`;

export const MenuContainer = styled.div`
	display: flex;
	flex-direction: row;
	position: absolute;
	top: 1.5rem;
	right: 1rem;
	z-index: var(--z-index-4);

	.columnsBtn {
		background: var(--background-3) !important;
		padding: 0;
	}

	body[dir='rtl'] & {
		right: unset;
		left: 1rem;
		top: 2rem;
	}

	@media ${Breakpoints.mobile} {
		display: none;
	}
`;

export const ColumnsMenu = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: var(--padding-2);
	background-color: var(--modal-bg-3);
	border-radius: var(--small-border-radius);
	min-width: 24rem;
	z-index: var(--z-index-2);
	box-shadow: var(--container-box-shadow);
	margin: 0 0 0 var(--margin-2);
	max-height: 44rem;
	overflow: auto;

	> .search-input {
		margin-bottom: var(--margin-1);
	}

	body[dir='rtl'] & {
		margin: 0 var(--margin-2) 0 0;
	}

	> button {
		flex: 1 0 100%;
	}

	@media ${Breakpoints.mobile} {
		margin: 0 !important;
	}
`;

export const ColumnsMenuItem = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	width: 100%;
	min-height: 4rem;
	border-radius: var(--small-border-radius);
	padding: var(--padding-2) var(--padding-3);
	z-index: var(--z-index-1);
	cursor: move;
	border-inline-start: 2px solid transparent;
	position: relative;

	> .icon {
		display: none;
		position: absolute;
		right: 0;
		background: inherit;

		body[dir='rtl'] & {
			right: unset;
			left: 0;
		}
	}

	:hover {
		background-color: var(--table-selected-color);

		> .icon {
			display: block;
		}
	}

	.typography {
		flex: 1;
		cursor: move;
		font-size: var(--paragraph3-font-size);
		color: var(--black);
		font-weight: 500;
		margin-left: var(--margin-2);

		body[dir='rtl'] & {
			margin-left: unset;
			margin-right: var(--margin-2);
		}
	}

	input {
		cursor: pointer;
	}

	&.selected {
		background-color: var(--table-active-color);
		border-inline-start: 2px solid var(--blue);
		border-radius: 0;
	}
`;

export const MobileFilterDot = styled.span`
	display: none;
	position: absolute;
	width: 0.75rem;
	height: 0.75rem;
	border-radius: 50%;
	background-color: var(--red-70);
	right: 0.75rem;
	top: 0.75rem;

	@media ${Breakpoints.mobile} {
		display: block;
	}
`;

export const MobileColumnDot = styled.span`
	display: none;
	position: sticky;
	width: 0.75rem;
	height: 0.75rem;
	border-radius: 50%;
	background-color: var(--red-70);

	@media ${Breakpoints.mobile} {
		display: block;
	}
`;

export const MobileMenuContainer = styled.div`
	display: none;
	flex-direction: row;
	justify-content: flex-end;
	gap: 1rem;
	width: 100%;
	background-color: var(--modal-bg-3);

	padding: 1rem 0.5rem;

	@media ${Breakpoints.mobile} {
		display: flex;
	}
`;

export const MobileFilterWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 1rem;
`;

export const MobileFilterMenuItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const FilterHeader = styled.div`
	display: flex;
	flex-direction: column;
	padding: 2rem 1rem;
	gap: 1rem;
`;

export const FilterMenuItemButtonContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 1rem;
`;

export const TreeBottomLine = styled.div`
	position: absolute;
	left: 1.5rem;
	width: ${({ margin, expandable }) => (expandable ? margin - 2.5 : margin)}rem !important;
	border-bottom: 1px solid var(--blue);

	body[dir='rtl'] & {
		left: unset;
		right: 1.5rem;
	}

	@media ${Breakpoints.mobile} {
		width: ${({ margin, expandable }) => (expandable ? margin - 2.5 : margin - 1)}rem !important;
	}
`;

export const RemoveFilterContainer = styled.div`
	display: inline-flex;
	justify-content: center;
	align-items: center;

	> button {
		width: auto;
	}
`;
